// Admin · Record. This file and server/admin_record.go are the whole section.
//
// THE WRITTEN RECORD OF THE WORK, in three tabs: the hours that were billed, the
// changelog joined to the commits behind it, and the git log itself.
//
// THE CHANGELOG TAB IS NOT A SECOND COPY OF /updates. It is the same items —
// imported from the same module the public page renders, so the two can never
// drift — with the commits each one was drawn from printed beside it. That join
// is precisely what the public page withholds on purpose (see updates-data.ts:
// no sha reaches the operator's bundle-visible prose), and it is the useful part
// here, because it is how a sentence written to a paying client gets checked
// against the change that produced it. An item with no commit recorded is worth
// seeing as such, so it is shown and counted rather than quietly omitted.
//
// THE OTHER TWO TABS READ BAKED FILES, and their failure mode is the feature.
// The deployed server has no git and no access to the time tracker; both tabs
// depend on a file being produced before the image is built. When that file is
// missing the tab does NOT render empty — it names the path it looked at and
// prints the command that makes the file. A blank panel that means "nothing
// generated this" but reads as "there is no history" is exactly the class of
// bug this admin area exists to kill.
//
// COMMIT BODIES ARE TEXT. This repository writes long, multi-paragraph commit
// messages full of quotes, angle brackets, ampersands and code fragments. Every
// one of them goes into the DOM through textContent, never innerHTML, and the
// line breaks are preserved by .ad-note-body's white-space rule rather than by
// converting anything to markup.

import { UPDATES, type UpdateKind } from "../updates-data.js";

/* ------------------------------------------------------------------ shapes */

interface RecordSource {
  path: string;
  present: boolean;
  problem?: string;
  fix?: string;
}

interface AuditItem {
  title: string;
  shas: string[];
}

interface AuditRelease {
  date: string;
  build: string;
  range: string;
  items: AuditItem[];
  internalCount: number;
}

interface ChangelogResp {
  source: RecordSource;
  releases: AuditRelease[];
}

interface GitCommit {
  sha: string;
  date: string;
  author: string;
  subject: string;
  body: string;
}

interface GitlogResp {
  source: RecordSource;
  generated?: string;
  head?: string;
  runningBuild: string;
  stale: boolean;
  truncated: boolean;
  commits: GitCommit[];
}

interface HoursReport {
  id: string;
  title: string;
  date?: string;
  bytes: number;
  modified: string;
}

interface HoursResp {
  source: RecordSource;
  reports: HoursReport[];
}

/* ------------------------------------------------------------------ pieces */

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (className !== undefined) n.className = className;
  if (text !== undefined) n.textContent = text;
  return n;
}

function card(heading: string): HTMLElement {
  const c = el("section", "ad-card");
  c.appendChild(el("h2", undefined, heading));
  return c;
}

function kv(list: HTMLDListElement, key: string, value: string, mono = false): void {
  list.appendChild(el("dt", undefined, key));
  list.appendChild(el("dd", mono ? "mono" : undefined, value));
}

async function getJSON<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: "same-origin" });
  if (!res.ok) throw new Error(`${url} returned ${res.status}`);
  return (await res.json()) as T;
}

/** A dead upstream degrades the panel; it never throws into a blank page. */
function failure(what: string, err: unknown): HTMLElement {
  const box = el("div", "ad-empty");
  box.appendChild(el("div", undefined, `Could not load ${what}.`));
  box.appendChild(el("div", "ad-when", err instanceof Error ? err.message : String(err)));
  return box;
}

/**
 * What to say when a baked file is not there. Three things, in this order: that
 * it is missing rather than empty, which path was consulted, and the command
 * that produces it. The last one is why this is a box of text and not a dash.
 */
function missing(src: RecordSource): HTMLElement {
  const box = el("div", "ad-empty");
  box.appendChild(
    el("div", undefined, src.problem ?? "This file is not present in the running deployment."),
  );
  box.appendChild(el("div", "ad-when", `looked for: ${src.path}`));
  if (src.fix !== undefined && src.fix !== "") {
    box.appendChild(el("div", undefined, "To produce it:"));
    box.appendChild(el("div", "ad-when", src.fix));
  }
  return box;
}

function fmtDay(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    timeZone: "UTC",
  });
}

function fmtStamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  return `${Math.round(bytes / 1024)} KB`;
}

/* --------------------------------------------------------------- changelog */

/** The operator's word for a kind, matching what /updates prints. */
const KIND_LABEL: Record<UpdateKind, string> = {
  added: "New",
  changed: "Changed",
  fixed: "Corrected",
  data: "Data",
};

function changelogBody(host: HTMLElement, data: ChangelogResp): void {
  // Keyed by date AND title: two releases can carry items with the same words,
  // and joining on the title alone would attribute one release's commits to the
  // other — a wrong citation, which is worse than no citation.
  const shasFor = new Map<string, string[]>();
  const meta = new Map<string, AuditRelease>();
  for (const rel of data.releases) {
    meta.set(rel.date, rel);
    for (const it of rel.items) shasFor.set(`${rel.date}\u0000${it.title}`, it.shas);
  }

  if (!data.source.present) host.appendChild(missing(data.source));

  if (UPDATES.length === 0) {
    host.appendChild(el("div", "ad-empty", "No release has been published to the Updates page yet."));
    return;
  }

  let claimed = 0;
  let uncited = 0;

  for (const rel of UPDATES) {
    const audit = meta.get(rel.date);
    const c = card(
      audit === undefined
        ? `${fmtDay(rel.date)} — no audit entry`
        : `${fmtDay(rel.date)} · build ${audit.build} · ${audit.range}`,
    );

    const table = el("table", "ad-table");
    const head = el("tr");
    head.append(el("th", undefined, "Kind"), el("th", undefined, "Told to the client"), el("th", undefined, "From"));
    table.appendChild(el("thead")).appendChild(head);
    const tbody = el("tbody");

    for (const it of rel.items) {
      claimed += 1;
      const shas = shasFor.get(`${rel.date}\u0000${it.title}`) ?? [];
      const row = el("tr");
      row.appendChild(el("td", "mono", KIND_LABEL[it.kind]));

      const said = el("td");
      said.appendChild(el("div", undefined, it.title));
      if (it.body !== undefined && it.body !== "") {
        said.appendChild(el("div", "ad-note-body", it.body));
      }
      row.appendChild(said);

      if (shas.length === 0) {
        // Said out loud. An item nobody can trace back to a change is the one
        // thing this join exists to surface, so it is never left blank.
        uncited += 1;
        const from = el("td");
        from.appendChild(el("span", "ad-state warn", "no commit recorded"));
        row.appendChild(from);
      } else {
        row.appendChild(el("td", "mono", shas.join("  ")));
      }
      tbody.appendChild(row);
    }

    table.appendChild(tbody);
    c.appendChild(table);

    const internal = audit?.internalCount ?? rel.internalCount;
    if (internal > 0) {
      c.appendChild(
        el("div", "ad-empty", `${internal} further change${internal === 1 ? "" : "s"} in this release had no operator-visible effect and were left out of the notes.`),
      );
    }
    host.appendChild(c);
  }

  const tally = el("div", "ad-when");
  tally.textContent =
    uncited === 0
      ? `${claimed} published item${claimed === 1 ? "" : "s"}, every one traceable to a commit.`
      : `${claimed} published item${claimed === 1 ? "" : "s"}, ${uncited} with no commit recorded — those cannot be checked against a change.`;
  host.appendChild(tally);
}

/* ------------------------------------------------------------------ gitlog */

const GITLOG_PAGE = 40;

function commitRow(c: GitCommit): HTMLElement {
  const row = el("tr");

  const when = el("td", "mono");
  when.appendChild(el("div", undefined, c.sha));
  when.appendChild(el("div", "ad-when", c.date === "" ? "" : fmtStamp(c.date)));
  if (c.author !== "") when.appendChild(el("div", "ad-when", c.author));
  row.appendChild(when);

  const msg = el("td");
  msg.appendChild(el("div", undefined, c.subject));
  // textContent, and .ad-note-body's white-space: pre-wrap keeps the paragraphs
  // and bullet lists intact. Never innerHTML: these bodies are full of angle
  // brackets, ampersands and quoted code.
  if (c.body.trim() !== "") msg.appendChild(el("div", "ad-note-body", c.body.trim()));
  row.appendChild(msg);

  return row;
}

function gitlogBody(host: HTMLElement, data: GitlogResp): void {
  const state = card("The baked git log");
  const body = el("div", "ad-body");
  const list = el("dl", "ad-kv");
  kv(list, "Cut from", data.head === undefined || data.head === "" ? "—" : data.head, true);
  kv(list, "Baked at", data.generated === undefined || data.generated === "" ? "—" : fmtStamp(data.generated), true);
  kv(list, "Running build", data.runningBuild, true);
  list.appendChild(el("dt", undefined, "State"));
  const stateDD = el("dd");
  if (!data.source.present) {
    stateDD.appendChild(el("span", "ad-state bad", "not baked"));
  } else if (data.stale) {
    stateDD.appendChild(el("span", "ad-state warn", "older than this deployment"));
  } else if (data.runningBuild === "dev") {
    stateDD.appendChild(el("span", "ad-state", "local build — nothing to compare against"));
  } else {
    stateDD.appendChild(el("span", "ad-state ok", "matches this deployment"));
  }
  list.appendChild(stateDD);
  body.appendChild(list);

  if (data.stale) {
    body.appendChild(
      el(
        "p",
        "ad-when",
        `This history was cut at ${data.head ?? "?"} but the server is running ${data.runningBuild}. ` +
          "Commits made since then are not below. Re-bake and deploy before citing anything here.",
      ),
    );
  }
  state.appendChild(body);
  host.appendChild(state);

  if (!data.source.present) {
    host.appendChild(missing(data.source));
    return;
  }
  if (data.commits.length === 0) {
    host.appendChild(el("div", "ad-empty", "The baked log is present but holds no commits."));
    return;
  }

  const c = card(`${data.commits.length} commit${data.commits.length === 1 ? "" : "s"}, newest first`);

  const bar = el("div", "ad-toolbar");
  const fld = el("label", "fld");
  // The shared field style is width:100% of its label, and as a bare flex item
  // that label collapses to the input's default size. Given a floor here rather
  // than a rule in the stylesheet, which is not this section's to edit.
  fld.style.minWidth = "240px";
  fld.appendChild(el("span", "lab", "Filter"));
  const filter = el("input");
  filter.type = "search";
  filter.placeholder = "sha, subject or body";
  fld.appendChild(filter);
  bar.appendChild(fld);
  const count = el("span", "ad-when ad-spacer");
  bar.appendChild(count);
  c.appendChild(bar);

  const table = el("table", "ad-table");
  const head = el("tr");
  head.append(el("th", undefined, "Commit"), el("th", undefined, "Message"));
  table.appendChild(el("thead")).appendChild(head);
  const tbody = el("tbody");
  table.appendChild(tbody);
  c.appendChild(table);

  const more = el("button", "btn");
  more.type = "button";
  c.appendChild(more);

  let shown = 0;
  let matches: GitCommit[] = data.commits;

  const paint = (): void => {
    const next = matches.slice(shown, shown + GITLOG_PAGE);
    for (const commit of next) tbody.appendChild(commitRow(commit));
    shown += next.length;
    count.textContent = `showing ${shown} of ${matches.length}`;
    more.textContent = `Show ${Math.min(GITLOG_PAGE, matches.length - shown)} more`;
    more.hidden = shown >= matches.length;
  };

  const refilter = (): void => {
    const q = filter.value.trim().toLowerCase();
    matches =
      q === ""
        ? data.commits
        : data.commits.filter(
            (x) =>
              x.sha.toLowerCase().includes(q) ||
              x.subject.toLowerCase().includes(q) ||
              x.body.toLowerCase().includes(q),
          );
    tbody.replaceChildren();
    shown = 0;
    if (matches.length === 0) {
      const row = el("tr", "muted");
      const cell = el("td", undefined, "Nothing in the baked log matches that.");
      cell.colSpan = 2;
      row.appendChild(cell);
      tbody.appendChild(row);
      count.textContent = "showing 0 of 0";
      more.hidden = true;
      return;
    }
    paint();
  };

  more.addEventListener("click", paint);
  filter.addEventListener("input", refilter);
  paint();

  if (data.truncated) {
    c.appendChild(
      el("div", "ad-empty", "This is not the whole history — the bake was capped, and older commits were left out."),
    );
  }
  host.appendChild(c);
}

/* ------------------------------------------------------------------- hours */

function hoursBody(host: HTMLElement, data: HoursResp): void {
  if (!data.source.present || data.reports.length === 0) {
    // Plainly, in words: nothing has been published. Not an empty table.
    const box = missing(data.source);
    if (data.source.present) {
      box.replaceChildren();
      box.appendChild(
        el("div", undefined, "No billable-hours report has been published into this deployment."),
      );
      box.appendChild(el("div", "ad-when", `looked in: ${data.source.path}`));
      box.appendChild(el("div", undefined, "To publish one:"));
      box.appendChild(el("div", "ad-when", data.source.fix ?? ""));
    }
    host.appendChild(box);
    return;
  }

  const c = card(`${data.reports.length} published report${data.reports.length === 1 ? "" : "s"}`);
  const table = el("table", "ad-table");
  const head = el("tr");
  head.append(
    el("th", undefined, "Period"),
    el("th", undefined, "Report"),
    el("th", undefined, "Published"),
    el("th", undefined, "Size"),
    el("th", undefined, ""),
  );
  table.appendChild(el("thead")).appendChild(head);
  const tbody = el("tbody");

  const frameCard = el("section", "ad-card");
  const frameHead = el("h2", undefined, "");
  const frameErr = el("div");
  const frame = el("iframe");
  // Fully sandboxed: no scripts, no same-origin, no forms. These pages are
  // generated by our own tool and contain no script today, but a file dropped
  // into a directory is not a thing to run in our own origin on trust.
  frame.setAttribute("sandbox", "");
  frame.setAttribute("loading", "lazy");
  frame.title = "Billable hours report";
  frame.style.width = "100%";
  frame.style.height = "72vh";
  frame.style.border = "0";
  frame.style.display = "block";
  frameCard.append(frameHead, frameErr, frame);
  frameCard.hidden = true;

  const show = (rep: HoursReport): void => {
    frameHead.textContent = rep.title;
    frameErr.replaceChildren();
    frameCard.hidden = false;
    frame.srcdoc = "";
    void fetch(`/api/admin/record?tab=hours&report=${encodeURIComponent(rep.id)}`, {
      credentials: "same-origin",
    })
      .then((res) => {
        if (!res.ok) throw new Error(`report returned ${res.status}`);
        return res.text();
      })
      .then((text) => {
        // srcdoc rather than src so the failure path above is reachable at all:
        // pointing the frame straight at the endpoint would render a JSON error
        // body as the report when something went wrong.
        frame.srcdoc = text;
      })
      .catch((err: unknown) => {
        frameHead.textContent = `${rep.title} — could not be loaded`;
        frameErr.replaceChildren(failure("that report", err));
      });
  };

  for (const rep of data.reports) {
    const row = el("tr");
    row.appendChild(el("td", "mono", rep.date === undefined || rep.date === "" ? "—" : rep.date));
    row.appendChild(el("td", undefined, rep.title));
    row.appendChild(el("td", "mono", fmtStamp(rep.modified)));
    row.appendChild(el("td", "mono", fmtSize(rep.bytes)));

    const actions = el("td");
    const view = el("button", "btn");
    view.type = "button";
    view.textContent = "View";
    view.addEventListener("click", () => {
      show(rep);
      frameCard.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    const open = el("a", "ad-when", "open in a new tab");
    open.href = `/api/admin/record?tab=hours&report=${encodeURIComponent(rep.id)}`;
    open.target = "_blank";
    open.rel = "noopener noreferrer";
    actions.append(view, document.createTextNode(" "), open);
    row.appendChild(actions);

    tbody.appendChild(row);
  }

  table.appendChild(tbody);
  c.appendChild(table);
  c.appendChild(
    el(
      "div",
      "ad-empty",
      "These are the pages handed to the client. They are generated outside this repository and published into it; " +
        "what is listed here is what this deployment carries, which is the only thing worth trusting about them.",
    ),
  );
  host.append(c, frameCard);
}

/* -------------------------------------------------------------------- tabs */

type TabID = "changelog" | "gitlog" | "hours";

interface Tab {
  id: TabID;
  label: string;
  load(host: HTMLElement): Promise<void>;
}

const TABS: readonly Tab[] = [
  {
    id: "changelog",
    label: "Changelog",
    async load(host) {
      changelogBody(host, await getJSON<ChangelogResp>("/api/admin/record?tab=changelog"));
    },
  },
  {
    id: "gitlog",
    label: "Git log",
    async load(host) {
      gitlogBody(host, await getJSON<GitlogResp>("/api/admin/record?tab=gitlog"));
    },
  },
  {
    id: "hours",
    label: "Hours",
    async load(host) {
      hoursBody(host, await getJSON<HoursResp>("/api/admin/record?tab=hours"));
    },
  },
];

export function mountAdminRecord(host: HTMLElement): void {
  // The sub-tabs are LOCAL STATE, not another hash. The admin shell already owns
  // window.location.hash to choose the section, and a second reader of the same
  // string would fight it on every click.
  let current: TabID = "changelog";

  const bar = el("div", "ad-toolbar");
  const panel = el("div", "admin-host");
  const buttons = new Map<TabID, HTMLButtonElement>();

  const paint = (): void => {
    for (const [id, b] of buttons) b.className = id === current ? "btn primary" : "btn";
    const loading = el("div", "ad-empty", "Loading…");
    panel.replaceChildren(loading);
    const tab = TABS.find((t) => t.id === current);
    if (tab === undefined) return;
    const mine = current;
    void tab
      .load(panel)
      .then(() => {
        // The loader appends beneath the placeholder; it goes once there is
        // something to replace it, and only if the reader has not moved on.
        if (mine !== current) return;
        loading.remove();
      })
      .catch((err: unknown) => {
        if (mine !== current) return;
        panel.replaceChildren(failure(`the ${tab.label.toLowerCase()}`, err));
      });
  };

  for (const t of TABS) {
    const b = el("button", "btn");
    b.type = "button";
    b.textContent = t.label;
    b.addEventListener("click", () => {
      if (current === t.id) return;
      current = t.id;
      paint();
    });
    buttons.set(t.id, b);
    bar.appendChild(b);
  }

  host.append(bar, panel);
  paint();
}
